import { Schema } from '@atlaskit/editor-prosemirror/model';
import { EditorState } from '@atlaskit/editor-prosemirror/state';
import { Decoration, EditorView } from '@atlaskit/editor-prosemirror/view';

import { createCommand } from './in-editor-streaming-plugin-factory';

const createHiddenInlineDecoration = (from: number, to: number) => {
	return Decoration.inline(
		from,
		to,
		{
			class: 'ai-inline-streaming-hidden',
			'data-testid': 'ai-inline-streaming-hidden',
		},
		{ key: 'inlineStreamingDecoration' },
	);
};

const createHiddenNodeDecoration = (from: number, to: number) => {
	return Decoration.node(
		from,
		to,
		{
			class: 'ai-inline-streaming-hidden',
			'data-testid': 'ai-inline-streaming-hidden',
		},
		{ key: 'inlineStreamingDecoration' },
	);
};

const createForceInlineDecoration = (from: number, to: number) => {
	return Decoration.node(
		from,
		to,
		{
			class: 'ai-inline-streaming-force-inline',
			'data-testid': 'ai-inline-streaming-force-inline',
		},
		{ key: 'inlineStreamingDecoration' },
	);
};

export const createAIResponseDecoration = (pos: number, state: EditorState) => {
	const widgetSpanElement = document.createElement('span');
	// Firefox needs content inside the span in order to add height to the element.
	// Otherwise, the height is set to 0 which prevents the start and
	// end widgets from rendering correctly and produces incorrect measurements
	// when we need to determine where to place the modal under the content.
	// Zero width spaces have been known to introduce some strange selection
	// behaviour in the Editor, however this approach works for our use case
	// in the ai-plugin where we do not expect users to be making selections
	// over ai-highlighted content in the same way they select regular content
	// in the Editor, this is also not intended to be a permanent fix post EAP.
	widgetSpanElement.appendChild(document.createTextNode('\u200b'));
	widgetSpanElement.setAttribute('data-testid', `ai-in-editor-widget`);
	widgetSpanElement.className = `ai-in-editor-widget`;

	const schema = new Schema({
		nodes: state.schema.spec.nodes,
		marks: state.schema.spec.marks,
		// topNode: state.doc.resolve(pos).nodeBefore?.type.name,
	});

	/**
	 * We want to inherit any node views from the parent document and retain all the functionality
	 */
	const innerEditorState = EditorState.create({
		doc: schema.topNodeType.createAndFill() || undefined,
		schema,
	}).reconfigure({ plugins: state.plugins.filter((plugin) => plugin.props.nodeViews) });

	const innerEditorView = new EditorView(widgetSpanElement, {
		// You can use any node as an editor document
		state: innerEditorState,
	});

	const widget = Decoration.widget(pos, widgetSpanElement, {
		side: 0,
		key: `aiInEditorWidgetDecoration`,
		destroy: () => {
			innerEditorView.destroy();
		},
	});

	return {
		widget,
		widgetSpanElement,
		innerEditorView,
	};
};

export const getHiddenRangeDecorations = ({
	state,
	initialPositions,
}: {
	state: EditorState;
	initialPositions: [number, number];
}) => {
	/**
	 * We add selection highlight decoration based on the current selection state of the document and the config item in the command palette
	 * If the selection is empty and the document is not empty -> we decorate the entire document unless the config item specifies otherwise
	 */

	const { startPos, endPos } = { startPos: initialPositions[0], endPos: initialPositions[1] };

	const decorations: Decoration[] = [];
	if (startPos !== endPos) {
		state.doc.nodesBetween(startPos, endPos, (node, pos, parent) => {
			if (node.isBlock) {
				if (pos < startPos && pos + node.nodeSize > endPos) {
					decorations.push(createHiddenInlineDecoration(startPos, endPos));
				} else if (pos > startPos && pos + node.nodeSize < endPos) {
					decorations.push(createHiddenNodeDecoration(pos, pos + node.nodeSize));
				} else if (pos < startPos) {
					decorations.push(createHiddenInlineDecoration(startPos, pos + node.nodeSize));
					if (pos + node.nodeSize > startPos) {
						decorations.push(createForceInlineDecoration(pos, pos + node.nodeSize));
					}
				} else if (pos > startPos && pos + node.nodeSize > endPos) {
					decorations.push(createHiddenInlineDecoration(pos, endPos));
					if (pos + node.nodeSize > endPos) {
						decorations.push(createForceInlineDecoration(pos, pos + node.nodeSize));
					}
				}
				return true;
			}
			return false;
		});
	}

	return decorations;
};

export const endInnerEditor = createCommand({
	type: 'END',
});
